/**
 * Integrytas Group - Identity & Access Management (IAM)
 * iam - client helpers for federated (SSO/OAuth) login on top of GoTrue.
 *
 *   listActiveProviders()  -> SSO buttons on the login screen
 *   startSso(provider)     -> GoTrue OAuth / SAML handshake
 *   resolveIamLogin()      -> after the redirect, JIT-provision + map roles
 */

import { supabase } from './supabase_client';
import { apiDb } from './api_client';

export interface IamProvider {
  iam_provider_id: number; internal_code: string; name: string;
  provider_type: 'OAUTH' | 'OIDC' | 'SAML' | 'PASSWORD';
  gotrue_provider: string | null; config: Record<string, unknown>;
}

export async function listActiveProviders(): Promise<IamProvider[]> {
  const { data, error } = await apiDb.from('iam_provider')
    .select('iam_provider_id, internal_code, name, provider_type, gotrue_provider, config')
    .eq('is_active', 'Y').neq('provider_type', 'PASSWORD').order('name');
  return error ? [] : (data ?? []) as IamProvider[];
}

/** Kick off the GoTrue handshake for a provider (OAuth or SAML SSO). */
export async function startSso(p: IamProvider): Promise<void> {
  if (p.provider_type === 'SAML') {
    const domain = (p.config?.domain as string) ?? '';
    await supabase.auth.signInWithSSO({ domain });
  } else if (p.gotrue_provider) {
    await supabase.auth.signInWithOAuth({
      provider: p.gotrue_provider as never,
      options: { scopes: (p.config?.scopes as string) ?? undefined },
    });
  }
}

/**
 * After a federated GoTrue session appears, link/just-in-time-provision the
 * csm_user_master and apply role mappings. No-op for email/password sessions.
 */
export async function resolveIamLogin(): Promise<{ ok: boolean; status?: string } | null> {
  const { data } = await supabase.auth.getSession();
  const u = data.session?.user;
  if (!u) return null;
  const gotrue = (u.app_metadata as { provider?: string } | undefined)?.provider;
  if (!gotrue || gotrue === 'email') return null;            // only federated logins

  const { data: prov } = await apiDb.from('iam_provider')
    .select('internal_code').eq('gotrue_provider', gotrue).eq('is_active', 'Y').maybeSingle();
  const code = (prov as { internal_code?: string } | null)?.internal_code;
  if (!code) return null;

  const { data: res } = await apiDb.rpc('iam_resolve_login', {
    p_provider_code: code, p_subject: u.id, p_email: u.email ?? null,
    p_claims: (u.user_metadata ?? {}) as Record<string, unknown>,
  });
  return (res ?? { ok: false }) as { ok: boolean; status?: string };
}
